/**
 * Accès au Web Storage qui ne jette jamais.
 *
 * Il y a DEUX modes d'échec distincts, et il faut couvrir les deux :
 *
 * 1. L'accès à `window.localStorage` **jette** — Safari en navigation privée
 *    (historiquement), Chrome quand le stockage de site est bloqué, certains
 *    navigateurs d'entreprise. C'est le cas auquel on pense spontanément.
 *
 * 2. `window.localStorage` **vaut `null`** — certaines WebView Android et
 *    navigateurs durcis exposent la propriété à null plutôt que de lever.
 *    Lire `.getItem` dessus donne alors « Cannot read properties of null
 *    (reading 'getItem') ». C'est ce second cas qui a produit
 *    Sentry 7740275467 : 5 événements le 2026-09-18 sur une fiche médecin,
 *    depuis le widget SahhaBot dont l'appel était le seul non protégé.
 *
 * Un `try/catch` local couvre techniquement les deux, mais il faut y penser à
 * chaque site d'appel — et il a suffi d'un oubli. Ce module est le point de
 * décision unique : importer `safeLocalStorage` / `safeSessionStorage` plutôt
 * que toucher `localStorage` directement.
 *
 * L'API est volontairement calquée sur `Storage` pour rester un remplacement
 * direct. Les écritures renvoient `false` en cas d'échec (quota dépassé,
 * stockage indisponible) pour l'appelant qui veut le savoir ; ignorer la valeur
 * de retour est parfaitement légitime.
 */

type StorageKind = "local" | "session";

/** Renvoie le Storage demandé, ou null s'il est indisponible — sans jamais jeter. */
function resolve(kind: StorageKind): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    const store = kind === "local" ? window.localStorage : window.sessionStorage;
    // Le `?? null` couvre le mode d'échec n°2 (propriété à null).
    return store ?? null;
  } catch {
    // Mode d'échec n°1 : l'accès lui-même jette.
    return null;
  }
}

function makeSafeStorage(kind: StorageKind) {
  return {
    getItem(key: string): string | null {
      const store = resolve(kind);
      if (!store) return null;
      try {
        return store.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key: string, value: string): boolean {
      const store = resolve(kind);
      if (!store) return false;
      try {
        store.setItem(key, value);
        return true;
      } catch {
        // Quota dépassé, ou stockage passé en lecture seule en cours de session.
        return false;
      }
    },
    removeItem(key: string): void {
      const store = resolve(kind);
      if (!store) return;
      try {
        store.removeItem(key);
      } catch {
        /* rien à faire : la clé est de toute façon inaccessible */
      }
    },
  };
}

export const safeLocalStorage = makeSafeStorage("local");
export const safeSessionStorage = makeSafeStorage("session");
